import { useEffect, useMemo, useState } from 'react';
import { BreakCategory } from '@sdk/settings/types';
import { TimesheetProjection } from '@sdk/timesheets/types/TimesheetProjection';
import useModalState from '@sdk/components/ui/modal/useModalState';
import useAuth from '@sdk/authentication/useAuth';
import getActualClockInTimes, { ClockInTime } from '@sdk/timeClock/utils/getActualClockInTimes';
import gettext from '@sdk/lang/gettext';
import useSlingApi from '@sdk/api/useSlingApi';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import useToast from '@sdk/common/toast/useToast';
import { getEstimatedBreaksDuration } from '@sdk/timesheets/helpers/getEstimatedBreaksDuration';

import useIsAutomatedBreaksFeatureFlagEnabled from '@web/automatedBreaks/hooks/useIsAutomatedBreaksFeatureFlagEnabled';
import { useAutomatedBreaksSettings } from '@web/settings/components/schedule/Breaks/breaks.hooks';
import { UseTimesheetViewModalProps } from '@web/timesheets/TimesheetViewModal/timesheetViewModal.types';
import { shouldDisplayAttestations } from '@web/timesheets/TimesheetViewModal/timesheetViewModal.utils';

export const useTimesheetProjectionsWithAttstations = (
  timesheetProjections: TimesheetProjection[]
) =>
  useMemo(
    () =>
      timesheetProjections.filter(({ breakAttestations }) => Boolean(breakAttestations?.length)),
    [timesheetProjections]
  );

export const useTimesheetViewModal = ({
  isTimesheetPreviewActive,
  onClose,
  shift,
  onTimesheetSave,
}: UseTimesheetViewModalProps) => {
  const { isVisible, open, close } = useModalState();
  const automatedBreaksFeatureEnabled = useIsAutomatedBreaksFeatureFlagEnabled();
  const [isTimesheetEditModeOn, setIsTimesheetEditModeOn] = useState(false);
  const [isHistoryView, setIsHistoryView] = useState(false);
  const { currentUser } = useAuth();
  const api = useSlingApi();
  const { showErrorToast, showSuccessToast } = useToast();
  const { automatedBreaks } = useAutomatedBreaksSettings();
  const modalConfig = { hideClose: true, noDismiss: true };
  const timesheetProjectionsWithAttstations = useTimesheetProjectionsWithAttstations(
    shift.timesheetProjections
  );

  useEffect(() => {
    // hook useses isTimesheetPreviewActive as parent (angular component) doesn't have open() method exposed.
    if (isTimesheetPreviewActive && !isVisible) {
      open();
    }
  }, [isTimesheetPreviewActive, isVisible, open]);

  const handleClose = () => {
    onClose();
    close();
    setIsTimesheetEditModeOn(false);
  };

  const handleEdit = () => {
    if (currentUser.isManager) {
      setIsTimesheetEditModeOn(true);
    }
  };

  const handleApprove = async () => {
    if (currentUser.isManager) {
      try {
        const ids = shift.timesheetProjections.map(({ id }) => id);
        await api.timesheets.approve(ids);
        showSuccessToast(gettext('Timesheet approved'));
        onTimesheetSave({ shiftId: shift.id, userId: shift.user.id });
        handleClose();
      } catch (e) {
        showErrorToast(getErrorForToast(e));
      }
    }
  };

  const handleMarkPending = async () => {
    if (currentUser.isManager) {
      try {
        const ids = shift.timesheetProjections.map(({ id }) => id);
        await api.timesheets.markAsPending(ids);
        showSuccessToast(gettext('Timesheet marked as pending'));
        onTimesheetSave({ shiftId: shift.id, userId: shift.user.id });
        handleClose();
      } catch (e) {
        showErrorToast(getErrorForToast(e));
      }
    }
  };

  const { usTimeFormat, worldClockEnabled } = currentUser.settings;
  const hasComment = Boolean(shift.assigneeNotes?.length);
  const clockInTimes = getActualClockInTimes({
    automatedBreaksFeatureEnabled,
    automatedBreaksSettings: automatedBreaks,
    areStandardBreaksPaid: currentUser.orgSettings.shiftBreakType === BreakCategory.paid,
    worldClockEnabled,
    timezone: shift.location.timezone,
    timesheetEntries: shift.timesheetEntries,
    timesheetProjections: shift.timesheetProjections,
    scheduledBreaksDuration: getEstimatedBreaksDuration({
      shift,
      automatedBreaksSettings: automatedBreaks,
      orgSettings: currentUser.orgSettings,
    }),
  });

  let clockInTimesBefore: ClockInTime[] = null;
  if ('timesheetEntriesBefore' in shift) {
    if (shift.timesheetEntriesBefore.length === 0) {
      clockInTimesBefore = [];
    } else {
      clockInTimesBefore = getActualClockInTimes({
        automatedBreaksFeatureEnabled,
        automatedBreaksSettings: automatedBreaks,
        areStandardBreaksPaid: currentUser.orgSettings.shiftBreakType === BreakCategory.paid,
        worldClockEnabled,
        timezone: shift.location.timezone,
        timesheetEntries: shift.timesheetEntriesBefore,
        timesheetProjections: shift.timesheetProjections,
        scheduledBreaksDuration: getEstimatedBreaksDuration({
          shift,
          automatedBreaksSettings: automatedBreaks,
          orgSettings: currentUser.orgSettings,
        }),
      });
    }
  }

  const hasAnyTimesheet = Boolean(clockInTimes.length) || Boolean(clockInTimesBefore?.length);
  const isCurrentUsersTimesheet = shift.user.id === currentUser.id;
  const isAttestationVisible = shouldDisplayAttestations(shift);

  return {
    isVisible,
    isTimesheetEditModeOn,
    setIsTimesheetEditModeOn,
    modalConfig,
    automatedBreaks,
    usTimeFormat,
    worldClockEnabled,
    clockInTimes,
    clockInTimesBefore,
    timesheetProjectionsWithAttstations,
    timesheetEntries: shift.timesheetEntries,
    isManager: currentUser.isManager,
    isCurrentUsersTimesheet,
    hasAnyTimesheet,
    isAttestationVisible,
    hasComment,
    handleClose,
    handleEdit,
    handleApprove,
    handleMarkPending,
    isHistoryView,
    setIsHistoryView,
  };
};
